<section class="overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-950 shadow-[0_10px_30px_rgba(15,23,42,0.05)]" aria-labelledby="manifest-append-heading">
  <header class="border-b border-slate-200 px-4 py-4 sm:px-5">
    <h3 id="manifest-append-heading" class="m-0 text-base font-black text-slate-950">Add to an existing lading</h3>
    <p class="mb-0 mt-1 text-xs text-slate-500">Find the customer shipment and choose which leg this freight follows.</p>
  </header>

  <div *ngIf="!customerReady" class="border-b border-blue-200 bg-blue-50 px-4 py-3 text-xs font-semibold text-blue-950">
    Select a customer in the shipment above to see their ladings.
  </div>

  <div class="grid lg:grid-cols-[minmax(20rem,0.85fr)_minmax(26rem,1.15fr)]">
    <div class="border-b border-slate-200 lg:border-b-0 lg:border-r">
      <div class="flex gap-2 border-b border-slate-200 p-3">
        <label class="sr-only" for="manifest-target-search">Search customer ladings</label>
        <input
          id="manifest-target-search"
          class="dma-input min-w-0 flex-1"
          type="search"
          maxlength="200"
          placeholder="Lading ID, BOL, or reference"
          [disabled]="!customerReady || appendBusy"
          [value]="searchText"
          (input)="onSearchInput($event)"
        >
        <button
          type="button"
          class="min-h-10 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition hover:border-blue-400 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
          [disabled]="!customerReady || targetState === 'loading' || appendBusy"
          (click)="refreshTargets()"
        >
          Refresh
        </button>
      </div>

      <div *ngIf="targetState === 'error'" class="m-3 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs font-semibold text-rose-900" role="alert">{{ targetError }}</div>
      <div *ngIf="targetState === 'loading'" class="flex items-center gap-2 p-4 text-xs font-semibold text-slate-600"><span class="dma-spinner" aria-hidden="true"></span>Finding customer ladings&hellip;</div>
      <div *ngIf="targetState === 'ready' && targets.length === 0" class="p-6 text-center text-xs text-slate-500">No matching ladings for this customer.</div>

      <div *ngIf="targets.length > 0" class="max-h-[30rem] overflow-y-auto">
        <button
          *ngFor="let target of targets; trackBy: trackTarget"
          type="button"
          class="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-3 border-b border-slate-100 px-4 py-3 text-left transition hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
          [disabled]="appendBusy"
          [class.bg-blue-50]="isTargetSelected(target)"
          (click)="selectTarget(target)"
        >
          <span class="min-w-0">
            <span class="block truncate text-xs font-black text-slate-950">{{ targetReference(target) }}</span>
            <span class="mt-1 block truncate text-[11px] text-slate-600">{{ laneLabel(target.lane.shipper) }} &rarr; {{ laneLabel(target.lane.consignee) }}</span>
          </span>
          <span class="self-center whitespace-nowrap text-[10px] font-bold text-slate-500">{{ target.leg_count }} {{ target.leg_count === 1 ? 'leg' : 'legs' }}</span>
        </button>
      </div>
    </div>

    <div class="min-w-0 p-4 sm:p-5">
      <div *ngIf="!selectedManifestUuid" class="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center">
        <p class="m-0 text-sm font-black text-slate-800">Choose a lading</p>
        <p class="mb-0 mt-1 text-xs text-slate-500">Its current route will appear here before anything is added.</p>
      </div>

      <div *ngIf="selectedManifestUuid" class="space-y-4">
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h4 class="m-0 text-sm font-black text-slate-950">Where should the new leg go?</h4>
            <p class="mb-0 mt-1 text-xs text-slate-500">Choose the active leg this freight follows.</p>
          </div>
          <button type="button" class="rounded-lg px-2 py-1.5 text-xs font-bold text-slate-500 transition hover:bg-slate-100 hover:text-slate-950 disabled:opacity-50" [disabled]="detailState === 'loading' || appendBusy" (click)="refreshDetail()">Refresh</button>
        </div>

        <div *ngIf="detailState === 'loading'" class="flex items-center gap-2 rounded-xl bg-slate-50 p-3 text-xs font-semibold text-slate-600"><span class="dma-spinner" aria-hidden="true"></span>Loading lading details&hellip;</div>
        <div *ngIf="detailState === 'error'" class="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs font-semibold text-rose-900" role="alert">{{ detailError }}</div>

        <ng-container *ngIf="detail as currentDetail">
          <div class="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs">
            <span><span class="text-slate-500">Lading</span> <strong class="ml-1 text-slate-950">#{{ currentDetail.data.manifest.manifest_id }}</strong></span>
            <span><span class="text-slate-500">Active legs</span> <strong class="ml-1 text-slate-950">{{ predecessorOptions.length }}</strong></span>
          </div>

          <label class="block text-xs font-bold text-slate-700">
            Add after
            <select class="dma-input mt-1.5 w-full" [value]="selectedPredecessorUuid" [disabled]="appendBusy || detailState !== 'ready'" (change)="onPredecessorChange($event)">
              <option value="">Choose an active leg</option>
              <option *ngFor="let leg of predecessorOptions; trackBy: trackLeg" [value]="leg.lading_uuid">{{ legLabel(leg) }}</option>
            </select>
          </label>

          <div class="overflow-hidden rounded-xl border border-slate-200">
            <div class="border-b border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Current route</div>
            <div *ngFor="let leg of predecessorOptions; trackBy: trackLeg" class="grid grid-cols-[4rem_minmax(0,1fr)_auto] gap-2 border-b border-slate-100 px-3 py-2 text-[11px] last:border-b-0">
              <span class="font-bold text-slate-500">{{ leg.manifest_leg_sequence === null ? 'Legacy' : 'Leg ' + leg.manifest_leg_sequence }}</span>
              <span class="truncate font-semibold text-slate-800">{{ laneLabel(leg.shipper) }} &rarr; {{ laneLabel(leg.consignee) }}</span>
              <span class="truncate text-right text-slate-500">{{ leg.lading_status || '' }}</span>
            </div>
          </div>
        </ng-container>

        <div class="rounded-xl border border-blue-200 bg-blue-50 p-3">
          <p class="m-0 text-xs text-blue-950">{{ appendPrerequisite }}</p>
          <button
            type="button"
            class="mt-3 min-h-11 w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-blue-300"
            [disabled]="appendDisabled"
            (click)="appendLeg()"
          >
            {{ appendState === 'refreshing' ? 'Checking lading…' : appendState === 'appending' ? 'Adding freight…' : hasPendingRetry ? 'Retry same request safely' : 'Add freight as new leg' }}
          </button>
        </div>

        <div *ngIf="appendState !== 'idle'" class="rounded-xl border p-3 text-xs font-semibold" [class.border-emerald-200]="appendState === 'success'" [class.bg-emerald-50]="appendState === 'success'" [class.text-emerald-900]="appendState === 'success'" [class.border-amber-200]="appendState === 'conflict'" [class.bg-amber-50]="appendState === 'conflict'" [class.text-amber-950]="appendState === 'conflict'" [class.border-rose-200]="appendState === 'error'" [class.bg-rose-50]="appendState === 'error'" [class.text-rose-900]="appendState === 'error'" [class.border-blue-200]="appendBusy" [class.bg-blue-50]="appendBusy" [class.text-blue-900]="appendBusy" aria-live="polite">
          {{ appendMessage }}
          <dl *ngIf="appendResult as result" class="mb-0 mt-2 grid grid-cols-2 gap-x-3 gap-y-1 font-mono text-[10px]">
            <dt>Lading UUID</dt><dd class="m-0 break-all text-right">{{ result.data.lading_uuid }}</dd>
            <dt>Manifest version</dt><dd class="m-0 text-right">v{{ result.data.manifest_version }}</dd>
          </dl>
        </div>
      </div>
    </div>
  </div>
</section>
